// ============================================================
// FAMILY CASHFLOW SHIELD — FINANCIAL CALCULATION ENGINE
// Pure functions. No UI. No storage. Fully unit-testable.
// All edge cases (zero, division-by-zero) handled safely.
// ============================================================

function num(v) {
  const n = Number(v);
  return isFinite(n) && !isNaN(n) ? n : 0;
}

// Safe division: returns 0 when denominator is 0/invalid.
function safeDiv(a, b) {
  if (b === 0 || !isFinite(b) || isNaN(b)) return 0;
  const r = a / b;
  return isFinite(r) && !isNaN(r) ? r : 0;
}

export function sumSideIncome(sideIncomes) {
  if (!Array.isArray(sideIncomes)) return 0;
  return sideIncomes.reduce((acc, s) => acc + num(s && s.amount), 0);
}

// Extract raw financial primitives from assessment data.
export function primitivesFromData(data = {}) {
  return {
    husband: num(data.husbandSalary),
    wife: num(data.wifeSalary),
    sideTotal: sumSideIncome(data.sideIncomes),
    totalDebt:
      num(data.mortgage) +
      num(data.vehicle) +
      num(data.creditCard) +
      num(data.otherDebt),
    living: num(data.livingExpenses),
    emergency: num(data.emergencyFund),
  };
}

// ============================================================
// FINANCIAL RESILIENCE SCORING MODEL V2.1 (FROZEN SPEC)
// Continuous / interpolated sub-scores. Do not recalibrate here;
// any change is a new model version.
//   C1 Emergency Fund .............. 30
//   C2 Debt Burden ................. 25
//   C3 Post-Shock Survival Coverage  30
//   C4 Side Income Vulnerability ... 15
//   Recovery horizon T = 12 months
// ============================================================
const RESILIENCE_RECOVERY_HORIZON_MONTHS = 12; // T

const clamp = (x, a, b) => Math.max(a, Math.min(b, x));

// C1 — Emergency Fund buffer (max 30). 0→6 months linear, saturates at 6.
function scoreEmergencyV21(emergencyMonths, essentialOutflow) {
  if (essentialOutflow <= 0) return 30; // no obligations => buffer fully sufficient
  return 30 * clamp(emergencyMonths / 6, 0, 1);
}

// C2 — Debt leverage (max 25). Full ≤20% of core income, zero ≥60%, linear between.
function scoreDebtBurdenV21(debtToCoreForScore) {
  return 25 * clamp((60 - debtToCoreForScore) / (60 - 20), 0, 1);
}

// C3 — Post-Shock Survival Coverage (max 30).
// After the largest income stream disappears, the household survives on
// remaining recurring income PLUS a monthly draw-down of liquid reserves
// amortized over the recovery horizon T. Coverage 0→0, 1.0→full.
function scoreShockCoverageV21(incomeAfterShock, essentialOutflow, emergency) {
  if (essentialOutflow <= 0) return 30;
  const coverage =
    (incomeAfterShock + emergency / RESILIENCE_RECOVERY_HORIZON_MONTHS) /
    essentialOutflow;
  return 30 * clamp(coverage, 0, 1);
}

// C4 — Side Income Vulnerability (max 15). Full ≤15% dependency, zero ≥60%.
function scoreSideVulnerabilityV21(dep) {
  return 15 * clamp((60 - dep) / (60 - 15), 0, 1);
}

// ============================================================
// Core metrics computed from primitives.
// This is the single source of truth reused by every scenario.
// ============================================================
export function metricsFrom(p) {
  const husband = num(p.husband);
  const wife = num(p.wife);
  const sideTotal = num(p.sideTotal);
  const totalDebt = num(p.totalDebt);
  const living = num(p.living);
  const emergency = num(p.emergency);

  const coreIncome = husband + wife;
  const totalFamilyIncome = coreIncome + sideTotal;
  const essentialOutflow = totalDebt + living;
  const monthlyBalance = totalFamilyIncome - essentialOutflow;

  const debtToTotalIncome = safeDiv(totalDebt, totalFamilyIncome) * 100;
  const debtToCoreIncome = safeDiv(totalDebt, coreIncome) * 100;
  const sideDependency = safeDiv(sideTotal, totalFamilyIncome) * 100;
  const emergencyMonths = safeDiv(emergency, essentialOutflow);

  // Runway (meaningful only when balance is negative)
  const isPositive = monthlyBalance >= 0;
  const monthlyDeficit = isPositive ? 0 : Math.abs(monthlyBalance);
  const survivalRunway = isPositive ? null : safeDiv(emergency, monthlyDeficit);

  // ---------- Resilience Score V2.1 ----------
  // Largest single income stream shock (aligns with product hero scenario).
  const largestStream = Math.max(husband, wife, sideTotal);
  const incomeAfterShock = totalFamilyIncome - largestStream;
  // Debt-to-core for scoring: no core income but debt => treat as fully leveraged.
  const debtToCoreForScore =
    coreIncome > 0 ? debtToCoreIncome : totalDebt > 0 ? 100 : 0;

  const c1 = scoreEmergencyV21(emergencyMonths, essentialOutflow);
  const c2 = scoreDebtBurdenV21(debtToCoreForScore);
  const c3 = scoreShockCoverageV21(incomeAfterShock, essentialOutflow, emergency);
  const c4 = scoreSideVulnerabilityV21(sideDependency);

  let score = Math.round(clamp(c1 + c2 + c3 + c4, 0, 100));

  // V2 safety overrides (unchanged): genuine, non-arbitrary fragility conditions.
  const deficitOverride = monthlyBalance < 0; // full income cannot cover essentials
  const noIncomeOverride = totalFamilyIncome <= 0; // no means to sustain
  if (noIncomeOverride) score = Math.min(score, 15);

  let status = "MERAH";
  if (score >= 70) status = "HIJAU";
  else if (score >= 45) status = "KUNING";

  const override = deficitOverride || noIncomeOverride;
  if (override) status = "MERAH";

  return {
    // primitives
    husband,
    wife,
    sideTotal,
    totalDebt,
    living,
    emergency,
    // derived
    coreIncome,
    totalFamilyIncome,
    essentialOutflow,
    monthlyBalance,
    isPositive,
    monthlyDeficit,
    survivalRunway,
    // ratios
    debtToTotalIncome,
    debtToCoreIncome,
    sideDependency,
    emergencyMonths,
    // score
    score: Math.round(score),
    status,
    scoreBreakdown: {
      emergency: Math.round(c1 * 10) / 10,
      debt: Math.round(c2 * 10) / 10,
      shock: Math.round(c3 * 10) / 10,
      sideVulnerability: Math.round(c4 * 10) / 10,
    },
    safetyOverride: override,
  };
}

export function computeBaseMetrics(data) {
  return metricsFrom(primitivesFromData(data));
}

// Plain-language reason a resilience result was forced to MERAH by a safety
// override. Returns null when no override is active. (Does NOT alter scoring.)
export function resilienceOverrideReason(m) {
  if (!m || !m.safetyOverride) return null;
  if (m.totalFamilyIncome <= 0)
    return "Belum ada pendapatan keluarga yang tercatat.";
  if (m.monthlyBalance < 0)
    return "Pengeluaran wajib bulanan lebih besar dari total pendapatan keluarga. Kekurangan ini ditutup dari dana darurat, sehingga status MERAH meskipun skornya terlihat lebih tinggi.";
  return null;
}

// The single biggest weakness driving the score down (largest gap-to-max
// among the four V2.1 components). Returns null when nothing stands out.
function topResilienceDriver(m) {
  const b = m.scoreBreakdown || {};
  const gaps = [
    { gap: 30 - (b.emergency || 0), txt: "dana darurat yang masih tipis dibanding pengeluaran wajib bulanan" },
    { gap: 25 - (b.debt || 0), txt: "beban cicilan yang tinggi terhadap penghasilan utama" },
    { gap: 30 - (b.shock || 0), txt: "sisa pendapatan yang belum cukup menutup pengeluaran wajib bila penghasilan utama terbesar berhenti" },
    { gap: 15 - (b.sideVulnerability || 0), txt: "ketergantungan besar pada penghasilan tambahan yang cenderung tidak tetap" },
  ];
  gaps.sort((a, b2) => b2.gap - a.gap);
  return gaps[0].gap > 0.5 ? gaps[0].txt : null;
}

// Headline explanation that references the REAL driver, not just the status.
export function resilienceHeadline(m) {
  if (!m) return "";
  // Override cases speak to the actual override cause.
  if (m.safetyOverride) {
    if (m.totalFamilyIncome <= 0)
      return "Belum ada pendapatan keluarga yang tercatat, sehingga ketahanan belum dapat dinilai.";
    if (m.monthlyBalance < 0)
      return "Pengeluaran wajib bulanan melebihi total pendapatan keluarga, jadi kekurangannya harus ditutup dari dana darurat setiap bulan.";
  }
  const driver = topResilienceDriver(m);
  if (m.status === "HIJAU")
    return "Ketahanan keluarga tergolong kuat. Pertahankan kebiasaan finansial yang sudah baik.";
  if (m.status === "KUNING")
    return driver
      ? `Kondisi cukup stabil. Area yang paling bisa diperkuat: ${driver}.`
      : "Kondisi cukup stabil, tetapi ada beberapa area yang dapat diperkuat.";
  // MERAH (non-override)
  return driver
    ? `Ketahanan keluarga masih rapuh, terutama karena ${driver}.`
    : "Ada beberapa area penting yang sebaiknya segera diperkuat.";
}

// ============================================================
// Scenario builder — applies modifications then recomputes.
// mods: {husbandSalary, wifeSalary, removeSideIncome,
//        debtMultiplier, emergencyAdd, livingReduction, debtReduction}
// ============================================================
export function computeScenario(data, mods = {}) {
  const p = primitivesFromData(data);
  const s = { ...p };

  if (mods.husbandSalary !== undefined) s.husband = num(mods.husbandSalary);
  if (mods.wifeSalary !== undefined) s.wife = num(mods.wifeSalary);
  if (mods.removeSideIncome) s.sideTotal = 0;
  if (mods.debtMultiplier !== undefined)
    s.totalDebt = s.totalDebt * num(mods.debtMultiplier);
  if (mods.emergencyAdd) s.emergency = s.emergency + num(mods.emergencyAdd);
  if (mods.livingReduction)
    s.living = Math.max(0, s.living - num(mods.livingReduction));
  if (mods.debtReduction)
    s.totalDebt = Math.max(0, s.totalDebt - num(mods.debtReduction));

  return metricsFrom(s);
}

// Which core salary is larger? Returns 'husband' | 'wife'
export function largestCoreSalary(data) {
  const h = num(data.husbandSalary);
  const w = num(data.wifeSalary);
  return w > h ? "wife" : "husband";
}

// DEFAULT HERO SCENARIO: loss of the largest core salary (side income remains)
export function heroScenario(data) {
  const target = largestCoreSalary(data);
  const mods = target === "husband" ? { husbandSalary: 0 } : { wifeSalary: 0 };
  const after = computeScenario(data, mods);
  return { target, after };
}

// Emergency fund target (6 months of essential outflow)
export function emergencyTarget(data) {
  const base = computeBaseMetrics(data);
  const target = base.essentialOutflow * 6;
  const gap = Math.max(0, target - base.emergency);
  return {
    target,
    current: base.emergency,
    gap,
    months: base.emergencyMonths,
  };
}

// ============================================================
// Rule-based action plan (max 3, prioritized by severity)
// ============================================================
export function actionPlan(data) {
  const base = computeBaseMetrics(data);
  const hero = heroScenario(data);
  const items = [];

  if (base.emergencyMonths < 3) {
    items.push({
      severity: 3,
      id: "emergency",
      title: "Perkuat dana darurat",
      issue: `Dana darurat saat ini setara ${base.emergencyMonths.toFixed(1).replace(".", ",")} bulan pengeluaran wajib.`,
      explanation:
        "Dana darurat idealnya menutup minimal 6 bulan pengeluaran wajib agar keluarga punya ruang bernapas saat pendapatan terganggu.",
      direction:
        "Sisihkan sebagian surplus bulanan secara rutin ke rekening dana darurat terpisah hingga mencapai target.",
    });
  }

  if (base.debtToCoreIncome > 40) {
    items.push({
      severity: 3,
      id: "debt",
      title: "Tinjau beban cicilan",
      issue: `Total cicilan setara ${Math.round(base.debtToCoreIncome)}% dari pendapatan utama keluarga.`,
      explanation:
        "Beban cicilan di atas 40% pendapatan utama membuat cashflow rentan ketika salah satu penghasilan berhenti.",
      direction:
        "Pertimbangkan menata ulang cicilan, mempercepat pelunasan utang berbunga tinggi, atau menunda penambahan cicilan baru.",
    });
  }

  if (base.sideDependency > 30) {
    items.push({
      severity: 2,
      id: "dependency",
      title: "Kurangi ketergantungan pada side income",
      issue: `Penghasilan tambahan menyumbang ${Math.round(base.sideDependency)}% dari total pendapatan keluarga.`,
      explanation:
        "Ketergantungan tinggi pada penghasilan tambahan yang cenderung tidak tetap membuat kestabilan keluarga lebih rapuh.",
      direction:
        "Perkuat pendapatan utama atau bangun dana darurat lebih besar sebagai penyangga bila side income berfluktuasi.",
    });
  }

  if (!hero.after.isPositive) {
    items.push({
      severity: 2,
      id: "expenses",
      title: "Kurangi kebutuhan bulanan minimum",
      issue:
        "Jika penghasilan utama terbesar berhenti, cashflow keluarga langsung mengalami defisit.",
      explanation:
        "Menurunkan pengeluaran wajib minimum memperpanjang daya tahan keluarga saat menghadapi kehilangan pendapatan.",
      direction:
        "Identifikasi pengeluaran rutin yang bisa ditekan sementara sehingga selisih defisit mengecil.",
    });
  }

  items.sort((a, b) => b.severity - a.severity);
  return items.slice(0, 3);
}

// ============================================================
// Build a complete report snapshot (persisted to history).
// ============================================================
export function buildReport(data) {
  const base = computeBaseMetrics(data);
  const hero = heroScenario(data);
  const sideStress = computeScenario(data, { removeSideIncome: true });
  const husbandLoss = computeScenario(data, { husbandSalary: 0 });
  const wifeLoss = computeScenario(data, { wifeSalary: 0 });
  const debtIncrease = computeScenario(data, { debtMultiplier: 1.1 });
  const target = emergencyTarget(data);
  const plan = actionPlan(data);

  return {
    id: `rpt_${Date.now()}`,
    createdAt: new Date().toISOString(),
    data: JSON.parse(JSON.stringify(data)),
    base,
    hero,
    sideStress,
    husbandLoss,
    wifeLoss,
    debtIncrease,
    target,
    plan,
  };
}
